// @flow
import { WEB_PATHS } from '@app/constants/paths';
import * as appTabsConstants from '@app/constants/tabs';
import RolesHelpers from '@user/helpers/RolesHelpers';
import type { RolesTypeValue } from '@beneficiary/types';
import type { Tab } from '@shared/Navigation/TabElement/types';
import type { ShortRegistered } from '@groupClass/types';

const { PAYMENTS, GROUP_SEND, PARTICIPANTS, GROUP_CLASS, GROUP_CLASS_INTERVENTIONS } = WEB_PATHS;

/**
 * Check if at least one Shape patient is registered in the group class
 * @param {ShortRegistered[]} registereds - List of registered participants
 * @returns {boolean} - True if at least one Shape patient is registered
 */
const hasShapePatient = (registereds: ShortRegistered[]): boolean =>
  registereds.some(
    (registered) =>
      registered.person?.shapeUser === true
      && registered.unsubscribedAt === null,
  );

export const getGroupClassesTabs = (roles: Array<RolesTypeValue>): Tab[] => {
  const tabs = [];

  const isAdmin = RolesHelpers.hasRoleAdmin(roles);
  const isManager = RolesHelpers.hasRoleManager(roles);
  const isCoach = RolesHelpers.hasRoleCoach(roles);
  const isBeneficiary = RolesHelpers.hasRoleBeneficiary(roles);

  tabs.push({
    ...appTabsConstants.GROUP_CLASSES_TAB,
    translationKey: isAdmin || isManager || isCoach ? 'navigation.myGroupClasses' : 'navigation.findGroupClass',
  });

  if (isBeneficiary) {
    tabs.unshift(appTabsConstants.BENEFICIARY_TAB);
    tabs.push(appTabsConstants.SHARED_SESSIONS_TAB);
    tabs.push(appTabsConstants.ONLINE_COURSES_TAB);
  }

  if (isAdmin) {
    tabs.push(appTabsConstants.PEOPLE_TAB);
  }

  return tabs;
};

const groupClassInformationTab = (id: number): Tab => ({
  name: 'infos',
  translationKey: 'groupClasses.groupClass.navigation.infos',
  isActive: false,
  link: GROUP_CLASS.replace(':classId', String(id)),
});

const groupClassInterventionTab = (id: number): Tab => ({
  name: 'interventions',
  translationKey: 'groupClasses.groupClass.navigation.interventions',
  isActive: false,
  link: GROUP_CLASS_INTERVENTIONS.replace(':classId', String(id)),
});

const groupClassPeopleTab = (id: number, requestsNumber: number): Tab => ({
  name: 'peoples',
  translationKey: 'user.participants',
  isActive: false,
  link: PARTICIPANTS.replace(':classId', String(id)),
  badge: requestsNumber,
});

const groupClassPaymentsTab = (id: number): Tab => ({
  name: 'payments',
  translationKey: 'groupClasses.groupClass.navigation.payments',
  isActive: false,
  link: PAYMENTS.replace(':classId', String(id)),
});

const groupClassSendingGroupTab = (id: number): Tab => ({
  name: 'group-send',
  translationKey: 'notification.send.toGroup',
  isActive: false,
  link: GROUP_SEND.replace(':classId', String(id)),
});

export const groupClassDetailedTabs = (id: number, requestsNumber: number, registereds: ShortRegistered[] = []): Tab[] => {
  const tabs = [];
  // Only show Intervention tab if at least one Shape patient is registered
  const showInterventionTab = hasShapePatient(registereds);

  tabs.push(groupClassInformationTab(id));
  tabs.push(groupClassPeopleTab(id, requestsNumber));
  if (showInterventionTab) {
    tabs.push(groupClassInterventionTab(id));
  }
  tabs.push(groupClassPaymentsTab(id));
  tabs.push(groupClassSendingGroupTab(id));

  return tabs;
};
